import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TablePagination,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { AbilityContext } from '../../config/ability-context';
import DialogConfirmation from '../../components/dialog-confirmation-not-button';
import EmailSettingsLayout from '../../components/email-settings-layout';
import {
  EmailSettingsEmptyState,
  EmailSettingsListSkeleton,
  EmailSettingsPageHeader,
  EmailSettingsSearch,
  EmailSettingsStatusChip,
  EmailSettingsTable,
  EmailSettingsTabs,
} from '../../components/email-settings-modern';
import * as Service from '../../services/emailtemplate.service';

const DEFAULT_ROWS_PER_PAGE = 10;
const localizeMetadata = (value, language) => {
  if (!value || typeof value === 'string') return value || '';
  const normalized = String(language || '').toLowerCase();
  const key = Object.keys(value).find(item => item.toLowerCase() === normalized)
    || Object.keys(value).find(item =>
      normalized.startsWith(item.slice(0, 2).toLowerCase())
    );
  return value[key] || value['pt-BR'] || value['en-US'] || '';
};

export default function EmailTemplateList() {
  const history = useHistory();
  const { lang } = useParams();
  const intl = useIntl();
  const ability = useContext(AbilityContext);
  const canList = ability?.can('List', 'EmailCustomSettings');
  const canAdd = ability?.can('Add', 'EmailCustomSettings');
  const canEdit = ability?.can('Edit', 'EmailCustomSettings');
  const canDelete = ability?.can('Delete', 'EmailCustomSettings');

  const [items, setItems] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [count, setCount] = useState(0);
  const [counts, setCounts] = useState({ all: 0, true: 0, false: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [countsLoading, setCountsLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    variant: 'success',
    message: '',
  });

  const message = id => intl.formatMessage({ id });
  const showNotification = (variant, text) =>
    setNotification({ open: true, variant, message: text });
  const isBusy = loading || countsLoading || busyId !== null;
  const goToCreate = () =>
    history.push(`/${lang}/email-template-registration`);
  const goToEdit = id => history.push(`/${lang}/email-template-edit/${id}`);

  const loadCounts = async () => {
    setCountsLoading(true);
    try {
      const [allResult, activeResult, inactiveResult, draftResult] = await Promise.all([
        Service.list({ page: 1, pagesize: 1 }),
        Service.list({ active: true, draft: false, page: 1, pagesize: 1 }),
        Service.list({ active: false, draft: false, page: 1, pagesize: 1 }),
        Service.list({ draft: true, page: 1, pagesize: 1 }),
      ]);
      setCounts({
        all: allResult.success ? allResult.count : 0,
        true: activeResult.success ? activeResult.count : 0,
        false: inactiveResult.success ? inactiveResult.count : 0,
        draft: draftResult.success ? draftResult.count : 0,
      });
    } finally {
      setCountsLoading(false);
    }
  };

  const loadPage = async (
    pageIndex,
    {
      nextSearch = appliedSearch,
      nextTab = tab,
      nextRowsPerPage = rowsPerPage,
    } = {}
  ) => {
    if (!canList) return;
    setLoading(true);
    try {
      const result = await Service.list({
        search: nextSearch,
        active: nextTab === 'true' ? true : nextTab === 'false' ? false : undefined,
        draft: nextTab === 'draft' ? true : ['true', 'false'].includes(nextTab) ? false : undefined,
        page: pageIndex + 1,
        pagesize: nextRowsPerPage,
      });
      if (result.success) {
        const nextPage =
          result.data.length === 0 && result.count > 0 && pageIndex > 0
            ? 0
            : pageIndex;
        if (nextPage !== pageIndex) {
          await loadPage(nextPage, {
            nextSearch,
            nextTab,
            nextRowsPerPage,
          });
          return;
        }
        setItems(result.data);
        setCount(result.count);
        setPage(pageIndex);
      } else {
        showNotification(
          'error',
          result.message || message('emailtemplates.load.error')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canList) return;
    loadPage(0, {
      nextSearch: '',
      nextTab: 'all',
      nextRowsPerPage: DEFAULT_ROWS_PER_PAGE,
    });
    loadCounts();
    Service.getPredefinedLayouts().then(result => {
      if (result.success) setLayouts(result.data || []);
    });
  }, [canList]);

  const refresh = async () => {
    await Promise.all([
      loadPage(page, {
        nextSearch: appliedSearch,
        nextTab: tab,
        nextRowsPerPage: rowsPerPage,
      }),
      loadCounts(),
    ]);
  };

  const changeTab = nextTab => {
    setTab(nextTab);
    loadPage(0, {
      nextSearch: appliedSearch,
      nextTab,
      nextRowsPerPage: rowsPerPage,
    });
  };

  const applySearch = () => {
    const nextSearch = search.trim();
    setAppliedSearch(nextSearch);
    loadPage(0, {
      nextSearch,
      nextTab: tab,
      nextRowsPerPage: rowsPerPage,
    });
  };

  const clearFilters = () => {
    setSearch('');
    setAppliedSearch('');
    setTab('all');
    loadPage(0, {
      nextSearch: '',
      nextTab: 'all',
      nextRowsPerPage: rowsPerPage,
    });
  };

  const toggleActive = async (event, row) => {
    event.stopPropagation();
    if (isBusy) return;
    setBusyId(row.id);
    try {
      const result = await Service.setActive(row.id, !row.active);
      if (result.success) {
        showNotification('success', message('emailtemplates.active.success'));
        await refresh();
      } else {
        showNotification(
          'error',
          result.message || message('emailtemplates.active.error')
        );
      }
    } finally {
      setBusyId(null);
    }
  };

  const openMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuItem(row);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const requestDelete = () => {
    setConfirmItem(menuItem);
    closeMenu();
  };

  const deleteItem = async () => {
    if (!confirmItem || isBusy) return;
    const id = confirmItem.id;
    setConfirmItem(null);
    setBusyId(id);
    try {
      const result = await Service.deleteById(id);
      if (result.success) {
        showNotification('success', message('emailtemplates.delete.success'));
        await refresh();
      } else {
        showNotification(
          'error',
          result.message || message('emailtemplates.delete.error')
        );
      }
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { key: 'id', label: message('emailsettings.id'), width: 80 },
    { key: 'name', label: message('emailtemplates.name') },
    { key: 'subject', label: message('emailtemplates.subject') },
    { key: 'layout', label: message('emailtemplates.list.mode'), width: 190 },
    { key: 'status', label: message('emailsettings.status'), width: 210 },
    { key: 'actions', label: '', width: 64, align: 'right' },
  ];

  if (!canList) {
    return (
      <EmailSettingsLayout>
        <Typography variant="h6">{message('emailsettings.forbidden')}</Typography>
      </EmailSettingsLayout>
    );
  }

  return (
    <EmailSettingsLayout
      notification={notification}
      closeNotification={() =>
        setNotification(current => ({ ...current, open: false }))
      }
    >
      <EmailSettingsPageHeader
        icon={<EmailOutlinedIcon />}
        title={message('emailtemplates.title')}
        subtitle={message('emailtemplates.subtitle')}
        refreshing={isBusy}
        refreshLabel={message('emailsettings.refresh')}
        onRefresh={refresh}
        primaryLabel={canAdd ? message('emailtemplates.new') : null}
        onPrimary={canAdd ? goToCreate : null}
        primaryDisabled={isBusy}
      />

      <EmailSettingsTabs
        value={tab}
        onChange={changeTab}
        disabled={isBusy}
        tabs={[
          {
            value: 'all',
            label: message('emailsettings.all'),
            count: counts.all,
            badgeColor: '#8a8a8a',
          },
          {
            value: 'true',
            label: message('emailsettings.active'),
            count: counts.true,
            badgeColor: '#5b9b1e',
          },
          {
            value: 'false',
            label: message('emailsettings.inactive'),
            count: counts.false,
            badgeColor: '#9e9e9e',
          },
          {
            value: 'draft',
            label: message('emailtemplates.draft.plural'),
            count: counts.draft,
            badgeColor: '#c58a20',
          },
        ]}
      />

      <EmailSettingsSearch
        value={search}
        onChange={setSearch}
        onSearch={applySearch}
        onClear={clearFilters}
        placeholder={message('emailtemplates.search.placeholder')}
        clearLabel={message('emailsettings.clear')}
        disabled={isBusy}
      />

      {loading ? (
          <EmailSettingsListSkeleton columns={6} />
      ) : items.length > 0 ? (
        <EmailSettingsTable
          columns={columns}
          rows={items}
          minWidth={1040}
          onRowClick={(_event, row) => !isBusy && goToEdit(row.id)}
          renderCells={row => [
            <Typography
              key="id"
              sx={{ fontSize: 13, color: '#999', fontWeight: 600 }}
            >
              #{row.id}
            </Typography>,
            <Typography
              key="name"
              sx={{ fontSize: 13, fontWeight: 600, color: '#333' }}
            >
              {row.isdraft ? row.name || message('emailtemplates.draft.untitled') : row.name}
            </Typography>,
            <Typography
              key="subject"
              sx={{
                fontSize: 13,
                color: '#555',
                maxWidth: 520,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.subject}
            </Typography>,
            <Chip
              key="layout"
              size="small"
              label={
                Number(row.layoutmode) === 2
                  ? message('emailtemplates.mode.html')
                  : localizeMetadata(
                      layouts.find(
                        layout => Number(layout.id) === Number(row.predefinedlayoutid)
                      )?.templatemetadata?.display_name
                        || layouts.find(
                          layout => Number(layout.id) === Number(row.predefinedlayoutid)
                        )?.name,
                      lang || intl.locale
                    ) || message('emailtemplates.mode.predefined')
              }
              sx={{
                maxWidth: 175,
                height: 24,
                bgcolor: Number(row.layoutmode) === 2 ? '#f2edff' : '#eef7e3',
                color: Number(row.layoutmode) === 2 ? '#5f4bb6' : '#5b9b1e',
                fontSize: 11,
                fontWeight: 600,
              }}
            />,
            <Box key="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {row.isdraft ? (
                <Chip
                  size="small"
                  label={message('emailtemplates.draft.singular')}
                  sx={{ height: 24, bgcolor: '#fff4df', color: '#9a6511', fontSize: 11, fontWeight: 700 }}
                />
              ) : (
                <EmailSettingsStatusChip
                  active={Boolean(row.active)}
                  activeLabel={message('emailsettings.active')}
                  inactiveLabel={message('emailsettings.inactive')}
                />
              )}
              {!row.isdraft && (busyId === row.id ? (
                <CircularProgress size={20} sx={{ color: '#8BC34A' }} />
              ) : (
                <Switch
                  size="small"
                  checked={Boolean(row.active)}
                  disabled={!canEdit || isBusy}
                  color="secondary"
                  onClick={event => event.stopPropagation()}
                  onChange={event => toggleActive(event, row)}
                />
              ))}
            </Box>,
            canEdit || canDelete ? (
              <IconButton
                key="actions"
                size="small"
                disabled={isBusy}
                onClick={event => openMenu(event, row)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ) : null,
          ]}
        />
      ) : (
        <EmailSettingsEmptyState
          icon={<EmailOutlinedIcon sx={{ fontSize: 36 }} />}
          title={message('emailtemplates.empty.title')}
          description={message('emailtemplates.empty.description')}
          actionLabel={canAdd ? message('emailtemplates.new') : null}
          onAction={canAdd ? goToCreate : null}
        />
      )}

      {count > 0 && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disabled={isBusy}
          onPageChange={(_event, nextPage) =>
            loadPage(nextPage, {
              nextSearch: appliedSearch,
              nextTab: tab,
              nextRowsPerPage: rowsPerPage,
            })
          }
          onRowsPerPageChange={event => {
            const nextRowsPerPage = Number(event.target.value);
            setRowsPerPage(nextRowsPerPage);
            loadPage(0, {
              nextSearch: appliedSearch,
              nextTab: tab,
              nextRowsPerPage,
            });
          }}
          labelRowsPerPage={message('emailsettings.rows.per.page')}
          labelDisplayedRows={({ from, to, count: total }) =>
            `${from}-${to} / ${total}`
          }
        />
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {canEdit && (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              const id = menuItem?.id;
              closeMenu();
              if (id) goToEdit(id);
            }}
            sx={{ fontSize: 13, gap: 1 }}
          >
            <EditOutlinedIcon fontSize="small" />
            {message('emailsettings.edit')}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            disabled={isBusy}
            onClick={requestDelete}
            sx={{ fontSize: 13, gap: 1, color: '#c0392b' }}
          >
            <DeleteOutlineIcon fontSize="small" />
            {message('emailsettings.delete')}
          </MenuItem>
        )}
      </Menu>

      {confirmItem && (
        <DialogConfirmation
          msg={message('emailsettings.delete.confirm.title')}
          optionToDelete={
            <span style={{ fontSize: '0.95rem', fontWeight: 400, color: '#444' }}>
              {message('emailtemplates.delete.confirm').replace(
                '{name}',
                confirmItem.name
              )}
            </span>
          }
          msgAgree={message('yes')}
          msgDisagree={message('no')}
          handleClose={() => setConfirmItem(null)}
          handleConfirmation={deleteItem}
        />
      )}
    </EmailSettingsLayout>
  );
}
